import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import { useState } from "react";
import { Goal, Project } from "../../api/Projects";
import { findColor, neutralColor, projectColorText } from "../../misc/projectColors";
import { TinyButton } from "../../misc/TinyButton";
import { ProjectCode } from "./ProjectCode";

const collapsedKey = "projectStrip.collapsed";

// ProjectStrip sits under the calendar and says what each project still has left in
// it: the open tasks due inside the range on screen, and the open tasks in total.
export function ProjectStrip(props: {
    projects: Project[];
    rangeLabel: string;
    selectedId?: number;
    selectedGoalId?: number;
    onSelect: (project: Project) => void;
    onSelectGoal: (goal: Goal) => void;
    onCreate: () => void;

    // unscheduledCount is the open tasks with no due date, which the last tile opens
    unscheduledCount: number;
    unscheduledSelected: boolean;
    onUnscheduled: () => void;

    // showTeam names each project's team, for the "All" workspace
    showTeam?: boolean;
}) {
    // the strip stays folded across visits once it's been folded
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(collapsedKey) === "true");

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        localStorage.setItem(collapsedKey, String(next));
    };

    return (
        <Box sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: collapsed ? 0 : 1 }}>
                <IconButton size="small" onClick={toggle} sx={{ ml: -1 }}
                            aria-label={collapsed ? "Show projects" : "Hide projects"}>
                    {collapsed ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
                <FolderOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">Projects</Typography>
                {collapsed && (
                    <Typography variant="caption" color="text.secondary">
                        {props.projects.length} active
                    </Typography>
                )}
                <TinyButton variant="outlined" color="primary"
                            startIcon={<Add sx={{ fontSize: 16 }} />}
                            onClick={props.onCreate}>
                    New project
                </TinyButton>
            </Box>

            {collapsed ? null : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {props.projects.map(project => (
                        <ProjectTile
                            key={project.id}
                            project={project}
                            rangeLabel={props.rangeLabel}
                            showTeam={props.showTeam}
                            selected={project.id === props.selectedId}
                            selectedGoalId={props.selectedGoalId}
                            onClick={() => props.onSelect(project)}
                            onSelectGoal={props.onSelectGoal}
                        />
                    ))}

                    <UnscheduledTile
                        count={props.unscheduledCount}
                        selected={props.unscheduledSelected}
                        onClick={props.onUnscheduled}
                    />
                </Box>
            )}
        </Box>
    );
}

// UnscheduledTile opens the list of open tasks with no due date
function UnscheduledTile(props: { count: number; selected: boolean; onClick: () => void }) {
    const color = neutralColor();

    return (
        <ButtonBase
            onClick={props.onClick}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.25,
                minWidth: 150,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                border: 2,
                borderColor: props.selected ? color.border : "transparent",
                bgcolor: color.bg,
                color: projectColorText(),
                "&:hover": { bgcolor: color.hover },
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Unscheduled</Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>{props.count} open</Typography>
        </ButtonBase>
    );
}

function ProjectTile(props: {
    project: Project;
    rangeLabel: string;
    showTeam?: boolean;
    selected: boolean;
    selectedGoalId?: number;
    onClick: () => void;
    onSelectGoal: (goal: Goal) => void;
}) {
    const color = findColor(props.project.color);
    const project = props.project;

    return (
        // the tile isn't one button: each goal row underneath is its own
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 150,
                maxWidth: 240,
                borderRadius: 1,
                border: 2,
                borderColor: props.selected ? color.border : "transparent",
                bgcolor: color.bg,
                color: projectColorText(),
                overflow: "hidden",
            }}
        >
            <Tooltip title={project.dueInRange + " due in " + props.rangeLabel
                + ", " + project.openCount + " open in total"}>
                <ButtonBase
                    onClick={props.onClick}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 0.25,
                        px: 1,
                        py: 0.75,
                        "&:hover": { bgcolor: color.hover },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, width: "100%", minWidth: 0 }}>
                        <ProjectCode code={project.code} color={project.color} />
                        <Typography variant="body2" noWrap
                                    sx={{ fontWeight: 500, flex: 1, minWidth: 0, textAlign: "left" }}>
                            {project.name}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        {project.dueInRange} due · {project.openCount} open
                        {props.showTeam && " · " + project.teamName}
                    </Typography>
                </ButtonBase>
            </Tooltip>

            {project.goals.filter(goal => !goal.archived).map(goal => (
                <Tooltip key={goal.id}
                         title={goal.dueInRange + " due in " + props.rangeLabel
                             + ", " + goal.openCount + " open in total"}>
                    <ButtonBase
                        onClick={() => props.onSelectGoal(goal)}
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.75,
                            px: 1,
                            py: 0.25,
                            borderTop: 1,
                            borderColor: color.border,
                            bgcolor: goal.id === props.selectedGoalId ? color.hover : undefined,
                            "&:hover": { bgcolor: color.hover },
                        }}
                    >
                        <Typography variant="caption" noWrap
                                    sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                            {goal.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75, flexShrink: 0 }}>
                            {goal.dueInRange} due · {goal.openCount} open
                        </Typography>
                    </ButtonBase>
                </Tooltip>
            ))}
        </Box>
    );
}
